/**
 * 제약사 목록 화면.
 * admin-ruda-web `pharmaceuticalCompanyInfo.js` 포팅.
 * - 검색·페이지네이션
 * - 엑셀은 현재 검색 조건 그대로 서버가 만들어 서명 URL을 준다
 */
import { isPharmaCompanyExportEmptyError } from '@/entities/pharma/api/pharma.api';
import {
  useExportPharmaCompanies,
  usePharmaCompanyList,
} from '@/entities/pharma/hooks/usePharmaCompanies';
import {
  formatCurrency,
  getContractStatusLabel,
} from '@/pages/pharma-companies/pharmaFormUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  pharmaCompanyDetailPath,
  ROUTE_PATHS,
} from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { formatDate } from '@/shared/lib/dateFormat';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Download, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const PHARMA_COMPANY_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  pharmaCompanyNameSearchKey: '',
};

export const PharmaCompaniesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(PHARMA_COMPANY_SEARCH_DEFAULTS);

  const [nameInput, setNameInput] = useState(values.pharmaCompanyNameSearchKey);
  const exportMutation = useExportPharmaCompanies();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportMutation.isPending;

  useEffect(() => {
    setNameInput(values.pharmaCompanyNameSearchKey);
  }, [values.pharmaCompanyNameSearchKey]);

  useEffect(() => {
    setGlobalLoading(isExporting, '제약사 목록 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const search = useMemo(
    () => ({
      pharmaCompanyNameSearchKey: values.pharmaCompanyNameSearchKey || undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = usePharmaCompanyList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const companies = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      pharmaCompanyNameSearchKey: nameInput.trim(),
      page: 1,
    });
  };

  const handleExport = async () => {
    try {
      const archive = await exportMutation.mutateAsync(search);
      if (!archive.downloadUrl) {
        toast.info('다운로드할 제약사가 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        `제약사 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isPharmaCompanyExportEmptyError(e)) {
        toast.info('다운로드할 제약사가 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Building2}
        title="제약사 관리"
        description="제약사 목록 조회 및 등록"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || isLoading || totalCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '처리 중...' : '엑셀 다운로드'}
            </Button>
            <MenuWriteGate>
              <Button
                onClick={() =>
                  navigate({ to: ROUTE_PATHS.PHARMA_COMPANY_NEW } as never)
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                제약사 추가
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label htmlFor="pharma-name">제약사명</Label>
            <Input
              id="pharma-name"
              placeholder="제약사명"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              className="w-56"
            />
          </div>
          <Button type="submit" className="ruda-button">
            검색
          </Button>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="제약사 목록을 불러오는 중..." />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState
            description={
              values.pharmaCompanyNameSearchKey
                ? '검색 조건에 맞는 제약사가 없습니다.'
                : '등록된 제약사가 없습니다.'
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>제약사명</TableHead>
                  <TableHead>계약 상태</TableHead>
                  <TableHead>계약 시작일</TableHead>
                  <TableHead>계약 종료일</TableHead>
                  <TableHead>최소 처방금액</TableHead>
                  <TableHead>필터링 정보 입력 여부</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow
                    key={company.pharmaCompanyId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: pharmaCompanyDetailPath(company.pharmaCompanyId),
                      } as never)
                    }
                  >
                    <TableCell className="font-medium">
                      {company.pharmaCompanyName}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getContractStatusLabel(
                          company.contractStartDate,
                          company.contractEndDate,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(company.contractStartDate)}</TableCell>
                    <TableCell>{formatDate(company.contractEndDate)}</TableCell>
                    <TableCell>
                      {formatCurrency(company.minPrescriptionAmount)}
                    </TableCell>
                    <TableCell>{company.filterInfo || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
